import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  CircularProgress,
  Box,
  SxProps,
  Theme,
  Typography,
} from "@mui/material";
import { AddOutlined as AddIcon } from "@mui/icons-material";
import { AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router";
import {
  DeadlineItem,
  DeadlineSubmission,
  EvidenceUpdate,
  PermitProcedureUpdate,
} from "../../../../types";
import {
  deadlinesApi,
  DeadlineScope,
} from "../../../../services/api/deadlines";
import { permitsApi } from "../../../../services/api/permits";
import { useUserOptions } from "../hooks/useUserOptions";
import { useToast } from "../../../../contexts/ToastContext";
import { usePagePermissions } from "../../../../auth/hooks/usePagePermissions";
import { useAuth } from "../../../../auth/AuthProvider";
import { useLoadState } from "../../../../hooks/useLoadState";
import { errorMessage } from "../../../../hooks/useProjectDeadlines";
import { getUrgency } from "../../../../utils/urgency";
import {
  deadlineEditing,
  deadlinePath,
  isOwnPersonal,
  isPersonal,
  referenceBreadcrumb,
} from "../../../../utils/deadlines";
import ConfirmDialog from "../../../common/feedback/ConfirmDialog";
import Button from "../../../common/inputs/Button";
import Section from "../../../layout/Section";
import ManualDeadlineDialog from "../../deadlines/ManualDeadlineDialog";
import ScopeToggle from "../../deadlines/ScopeToggle";
import DeadlineAgenda, { AgendaItem } from "./DeadlineAgenda";
import { colors } from "../../../../styles/colors";

/** Ein Projekt im Fokus der Übersicht – die Kachel zeigt dann nur seine Termine */
export interface ReminderFocus {
  projectId: string;
  /** Ob man alle Termine des Projekts sehen darf; sonst ein Hinweis */
  seesAll: boolean;
}

interface ReminderSectionProps {
  sx?: SxProps<Theme>; // Optional: Custom styles für die Section-Kachel (z. B. Höhe an Karte angleichen)
  /**
   * Im Fokus nur die offenen Termine dieses Projekts, zuerst die des ganzen Teams. „Meine“ /
   * „Team“ filtert weiter.
   */
  focus?: ReminderFocus | null;
  /** Meldet, dass sich ein Termin geändert hat – etwa für die Problemsignale */
  onChanged?: () => void;
  /**
   * Antrag des Projekts im Fokus, an dem ein Termin angelegt werden soll – etwa aus dem Menü
   * seiner Unterzeile. Öffnet den Dialog mit diesem Antrag als Bezug.
   */
  createAtProcedure?: string | null;
  /** Der so geöffnete Dialog ist zu */
  onCreateClosed?: () => void;
}

const ReminderSection: React.FC<ReminderSectionProps> = ({
  sx,
  focus,
  onChanged,
  createAtProcedure,
  onCreateClosed,
}) => {
  const navigate = useNavigate();
  const focusProjectId = focus?.projectId ?? null;
  // Im Fokus geht es um das Projekt, nicht um die eigenen Termine: Die Kachel beginnt dort mit
  // „Team“. Die Wahl außerhalb des Fokus bleibt davon unberührt und gilt danach wieder.
  const [overviewScope, setOverviewScope] = useState<DeadlineScope>("mine");
  const [focusScope, setFocusScope] = useState<DeadlineScope>("team");
  const inFocus = !!focusProjectId;
  // Zurückgestellt beim Verlassen, nicht beim Betreten: So lädt der Fokus gleich mit „Team“.
  useEffect(() => {
    if (!inFocus) setFocusScope("team");
  }, [inFocus]);
  const scope = inFocus ? focusScope : overviewScope;
  const setScope = inFocus ? setFocusScope : setOverviewScope;
  const [deadlines, setDeadlines] = useState<DeadlineItem[]>([]);
  const [creating, setCreating] = useState(false);
  /** Geöffneter manueller Termin – erst zum Lesen, im Dialog auch zum Bearbeiten */
  const [selected, setSelected] = useState<DeadlineItem | null>(null);
  const [deleting, setDeleting] = useState<DeadlineItem | null>(null);
  const { showError, showSuccess, showUndo } = useToast();
  // Termine pflegt, wer Projekte bearbeiten darf – dieselbe Berechtigung wie die Übersicht.
  const { canEdit } = usePagePermissions("project-overview");
  // Fristen an Antrag und Nachweis und alle abgeleiteten gehören zur Genehmigung.
  const { canEdit: canEditPermits } = usePagePermissions(
    "project-permit-evidences",
  );
  const users = useUserOptions();
  const { user } = useAuth();
  // Termine der Startseite gehören zum eigenen Mandanten (`projects:read`); Externe pflegen
  // Termine an den freigegebenen Projekten selbst.
  const canAddDeadlines = !!user?.permissions.includes("projects:read");
  // „Team“ gibt es nur mit dem Recht Teamübersicht; ohne es zeigt die Kachel die eigenen Termine
  // und keinen Umschalter. Der Server hält sich ebenso daran.
  const canSeeTeam = !!user?.permissions.includes("deadlines:team");
  // Spinner nur beim ersten Abruf je Meine/Team und Projekt – nach dem Abhaken bleibt die
  // Liste stehen.
  const { loading, ladenStarten, ladenBeenden } = useLoadState(
    `${scope}:${focusProjectId ?? ""}`,
  );

  const loadDeadlines = useCallback(async () => {
    ladenStarten();
    try {
      if (focusProjectId) {
        // Die offenen Termine des Projekts – dieselben wie in seinen Termin-Sections. „Meine“
        // filtert hier, der Server liefert sie ohne Unterscheidung.
        const all = await deadlinesApi.getProjectDeadlines(focusProjectId);
        setDeadlines(
          all.filter(
            (d) =>
              !d.done_at &&
              (scope === "team" || d.verantwortlich?.external_id === user?.id),
          ),
        );
      } else {
        setDeadlines(await deadlinesApi.getDeadlines({ scope }));
      }
    } catch {
      setDeadlines([]);
    } finally {
      ladenBeenden();
    }
  }, [scope, focusProjectId, user?.id, ladenStarten, ladenBeenden]);

  /** Neu laden und Bescheid geben – die Problemsignale hängen an denselben Terminen */
  const reload = useCallback(async () => {
    await loadDeadlines();
    onChanged?.();
  }, [loadDeadlines, onChanged]);

  useEffect(() => {
    loadDeadlines();
  }, [loadDeadlines]);

  // Zur Auswahl beim Verantwortlichen; ohne Liste bleibt er in der Zeile unveränderlich.

  const agendaItems: AgendaItem[] = useMemo(
    () =>
      deadlines.map((d) => {
        const editing = deadlineEditing(d, {
          userId: user?.id,
          canEditProjects: canEdit,
          canEditPermits,
        });
        return {
          id: d.id,
          title: d.bezeichnung,
          dueDate: d.zieltermin,
          urgency: getUrgency(d.zieltermin),
          breadcrumb: referenceBreadcrumb(d),
          responsibleName:
            d.verantwortlich?.display_name || d.verantwortlich?.username,
          responsibleId: d.verantwortlich?.external_id,
          note: d.note,
          problem: !!d.problem,
          personal: isPersonal(d),
          canEditDueDate: editing.dueDate,
          canEditResponsible: editing.responsible,
          canEditNote: editing.note,
        };
      }),
    [deadlines, user?.id, canEdit, canEditPermits],
  );

  /**
   * Datum oder Verantwortlichen aus der Zeile speichern. Eine abgeleitete Frist ändert das Feld
   * am Nachweis, das sie trägt; eine manuelle bzw. persönliche wird mit allen Angaben neu
   * gespeichert, so wie der Dialog es tut.
   */
  const saveInline = async (
    item: AgendaItem,
    change: {
      dueDate?: string;
      responsibleId?: string;
      note?: string | null;
      problem?: boolean;
    },
  ) => {
    const deadline = deadlines.find((d) => d.id === item.id);
    if (!deadline) return;
    const dueDate = change.dueDate ?? deadline.zieltermin ?? "";
    // Gespeichert wird der ganze Termin – das Kennzeichen bleibt, solange es niemand ändert.
    const problem = change.problem ?? !!deadline.problem;
    try {
      if (deadline.source === "derived" && change.problem !== undefined) {
        // Am Nachweis ist das Problem eine Aktion mit Eintrag in der Nachweishistorie.
        await permitsApi.uebergangEvidence(
          deadline.project_id!,
          deadline.permit_procedure_id!,
          deadline.evidence_id!,
          change.problem
            ? {
                aktion: "flag_problem",
                felder: { note: deadline.note ?? null },
              }
            : { aktion: "clear_problem" },
        );
      } else if (deadline.source === "derived" && !deadline.evidence_id) {
        // Frist des Antragsstatus: das Feld am Antrag bzw. dessen Bearbeitung (ADR 011).
        await permitsApi.updatePermitProcedure(
          deadline.project_id!,
          deadline.permit_procedure_id!,
          change.dueDate
            ? ({
                [deadline.due_field!]: change.dueDate,
              } as PermitProcedureUpdate)
            : { bearbeitung_user_id: change.responsibleId ?? null },
        );
      } else if (deadline.source === "derived") {
        const data: EvidenceUpdate = change.dueDate
          ? ({ [deadline.due_field!]: change.dueDate } as EvidenceUpdate)
          : change.responsibleId
            ? { verantwortlich_id: change.responsibleId }
            : { note: change.note ?? null };
        await permitsApi.updateEvidence(
          deadline.project_id!,
          deadline.permit_procedure_id!,
          deadline.evidence_id!,
          data,
        );
      } else if (isPersonal(deadline)) {
        await deadlinesApi.updatePersonalDeadline(deadline.id, {
          title: deadline.bezeichnung,
          due_date: dueDate,
          note: change.note !== undefined ? change.note : deadline.note,
          problem,
        });
      } else {
        await deadlinesApi.updateManualDeadline(
          deadline.project_id!,
          deadline.id,
          {
            title: deadline.bezeichnung,
            due_date: dueDate,
            responsible_user_id:
              change.responsibleId ?? deadline.verantwortlich?.external_id,
            note: change.note !== undefined ? change.note : deadline.note,
            problem,
            permit_procedure_id: deadline.permit_procedure_id,
            evidence_id: deadline.evidence_id,
          },
        );
      }
      await reload();
      showSuccess("Gespeichert");
    } catch (err) {
      showError(errorMessage(err, "Termin konnte nicht gespeichert werden"));
    }
  };

  /** Pflegen: den eigenen persönlichen Termin immer, einen Projekttermin mit Schreibrecht */
  const mayEdit = (deadline: DeadlineItem): boolean =>
    isPersonal(deadline) ? isOwnPersonal(deadline, user?.id) : canEdit;

  /** Manuelle Termine öffnen sich im Dialog, abgeleitete führen zu ihrem Bezug */
  const handleItemClick = (item: AgendaItem) => {
    const deadline = deadlines.find((d) => d.id === item.id);
    if (!deadline) return;
    const path = deadline.source === "manual" ? null : deadlinePath(deadline);
    if (path) navigate(path);
    else setSelected(deadline);
  };

  const saveDone = (deadline: DeadlineItem, done: boolean) =>
    isPersonal(deadline)
      ? deadlinesApi.setPersonalDeadlineDone(deadline.id, done)
      : deadlinesApi.setManualDeadlineDone(
          deadline.project_id!,
          deadline.id,
          done,
        );

  /** Erledigen wirkt sofort, ohne Rückfrage – die Meldung bietet „Rückgängig“ */
  const handleSetDone = async (deadline: DeadlineItem, done: boolean) => {
    setSelected(null);
    try {
      await saveDone(deadline, done);
      await reload();
      showUndo(
        done ? "Termin erledigt" : "Termin wieder geöffnet",
        async () => {
          try {
            await saveDone(deadline, !done);
            await reload();
          } catch (err) {
            showError(errorMessage(err, "Termin konnte nicht geändert werden"));
          }
        },
      );
    } catch (err) {
      showError(errorMessage(err, "Termin konnte nicht abgehakt werden"));
    }
  };

  /** Neuer Termin – persönlich oder am Projekt – oder Änderung des geöffneten */
  const handleSubmit = async (
    submission: DeadlineSubmission,
  ): Promise<boolean> => {
    try {
      if (submission.kind === "personal") {
        if (selected) {
          await deadlinesApi.updatePersonalDeadline(
            selected.id,
            submission.data,
          );
        } else {
          await deadlinesApi.createPersonalDeadline(submission.data);
        }
      } else if (selected) {
        await deadlinesApi.updateManualDeadline(
          submission.projectId,
          selected.id,
          submission.data,
        );
      } else {
        await deadlinesApi.createManualDeadline(
          submission.projectId,
          submission.data,
        );
      }
      await reload();
      return true;
    } catch (err) {
      showError(errorMessage(err, "Termin konnte nicht gespeichert werden"));
      return false;
    }
  };

  const handleDelete = async (deadline: DeadlineItem) => {
    try {
      if (isPersonal(deadline)) {
        await deadlinesApi.deletePersonalDeadline(deadline.id);
      } else {
        await deadlinesApi.deleteManualDeadline(
          deadline.project_id!,
          deadline.id,
        );
      }
      setSelected(null);
      await reload();
    } catch (err) {
      showError(errorMessage(err, "Termin konnte nicht gelöscht werden"));
    }
  };

  const actions = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {/* Im Fokus filtert der Umschalter die Termine des Projekts – die sieht jeder, der das
          Projekt sieht, so wie in dessen Termin-Sections. */}
      {(canSeeTeam || focus) && (
        <ScopeToggle value={scope} onChange={setScope} />
      )}
      {/* Hauptaktion der Kachel, gefüllt (components/CLAUDE.md). Persönliche Termine
          braucht kein Recht an einem Projekt; einen Projekttermin lehnt der Server ab, wenn das
          Recht am Bezug fehlt. */}
      {canAddDeadlines && (
        <Button
          variant="primary"
          startIcon={<AddIcon />}
          onClick={() => setCreating(true)}
        >
          Termin anlegen
        </Button>
      )}
    </Box>
  );

  return (
    <Section
      // Ohne Projektnamen: Den zeigen Header, ausgewählte Zeile und der Pfad am Termin
      title="Termine"
      actions={actions}
      scrollable
      noPadding
      sx={sx}
    >
      {focus && !focus.seesAll && (
        <Typography
          variant="caption"
          sx={{
            display: "block",
            color: colors.text.secondary,
            px: 3,
            pt: 1.5,
            pb: 1,
          }}
        >
          Termine an Anträgen und Nachweisen ohne Berechtigung sind
          ausgeblendet.
        </Typography>
      )}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <AnimatePresence mode="wait">
          <DeadlineAgenda
            key={`${scope}:${focusProjectId ?? ""}`}
            items={agendaItems}
            users={users}
            onItemClick={handleItemClick}
            onDueDateChange={(item, dueDate) => saveInline(item, { dueDate })}
            onResponsibleChange={(item, responsibleId) =>
              saveInline(item, { responsibleId })
            }
            onNoteChange={(item, note) => saveInline(item, { note })}
            onProblemChange={(item, problem) => saveInline(item, { problem })}
          />
        </AnimatePresence>
      )}
      <ManualDeadlineDialog
        open={creating || !!selected || !!createAtProcedure}
        presetProjectId={
          (creating || createAtProcedure) && focusProjectId
            ? focusProjectId
            : undefined
        }
        presetPermitProcedureId={createAtProcedure}
        deadline={selected}
        canEdit={!!selected && mayEdit(selected)}
        showReferenceLink
        onClose={() => {
          setCreating(false);
          setSelected(null);
          if (createAtProcedure) onCreateClosed?.();
        }}
        onSubmit={handleSubmit}
        onSetDone={
          selected ? (done) => handleSetDone(selected, done) : undefined
        }
        onDelete={selected ? () => setDeleting(selected) : undefined}
      />
      <ConfirmDialog
        open={!!deleting}
        title="Termin löschen"
        message={
          deleting ? `Termin „${deleting.bezeichnung}" wirklich löschen?` : ""
        }
        confirmLabel="Löschen"
        onConfirm={() => {
          if (deleting) handleDelete(deleting);
          setDeleting(null);
        }}
        onCancel={() => setDeleting(null)}
      />
    </Section>
  );
};

export default ReminderSection;
